<style scoped>
.modal.show {
	background-color: rgba(0, 0, 0, 0.5);
}

/*
 * One mechanism at every size: the dialog is capped to the viewport so its footer
 * always stays reachable, and the modal body is the single scroll surface —
 * scrolling only when the message genuinely does not fit. A max-height rather
 * than a fixed height keeps the dialog shrinking to its content on a tall screen.
 *
 * The cap sits on .modal-content because that is the flex column Bootstrap lays
 * the parts out in, and because capping .modal-dialog would not work: the
 * .modal-dialog-centered class sets min-height: calc(100% - 2 * margin) on the
 * dialog, and a min-height beats a max-height. The margin is part of the sum
 * because Bootstrap applies it above and below the dialog, and it is 0.5rem on
 * phones but 1.75rem from the sm breakpoint up.
 */
.modal-content {
	max-height: calc(100vh - var(--bs-modal-margin) * 2);
}

/*
 * min-height: 0 is what lets this flex item shrink below its content and scroll;
 * the header and footer are flex-shrink: 0, so only the body ever moves.
 */
.modal-body {
	overflow-y: auto;
	min-height: 0;
}

/*
 * The box is sized to its content from scrollHeight (see fitMessageBox) and is
 * never capped, so it has no scrollbar of its own at any size — when the message
 * is taller than the dialog, the modal body above is what scrolls. Manual
 * resizing is off for the same reason: a dragged height would fight the auto-fit
 * and snap back on the next keystroke. min-height keeps an emptied box from
 * collapsing to a single line (~the old four-row height).
 */
#mosquito-check-message {
	resize: none;
	min-height: 7rem;
}
</style>

<template>
	<div
		class="modal fade"
		:class="{ 'show d-block': show }"
		tabindex="-1"
		v-show="show"
		@click.self="handleCancel"
	>
		<div class="modal-dialog modal-dialog-centered">
			<div class="modal-content">
				<div class="modal-header">
					<h5 class="modal-title">
						<i class="bi bi-mosquito me-2"></i>Send Mosquito Check
					</h5>
					<button
						type="button"
						class="btn-close"
						@click="handleCancel"
					></button>
				</div>
				<div class="modal-body">
					<p v-if="contactName" class="text-muted small mb-3">
						Sending to <strong>{{ contactName }}</strong>
					</p>
					<div class="mb-3">
						<label class="form-label fw-semibold" for="mosquito-check-message"
							>Message</label
						>
						<textarea
							id="mosquito-check-message"
							ref="messageBox"
							class="form-control"
							rows="4"
							v-model="message"
							@input="fitMessageBox"
						></textarea>
						<div class="form-text">
							<code>{link}</code> is replaced with the resident's link
							automatically. Leave blank to send the district's default message.
						</div>
					</div>
					<div v-if="error" class="alert alert-danger" role="alert">
						{{ error }}
					</div>
				</div>
				<div class="modal-footer">
					<button
						type="button"
						class="btn btn-outline-secondary"
						@click="handleCancel"
					>
						Cancel
					</button>
					<ButtonLoading
						@click="handleSend"
						:loading="sending"
						icon="bi-mosquito"
						text="Send Mosquito Check"
						variant="primary"
					/>
				</div>
			</div>
		</div>
	</div>
	<div
		class="modal-backdrop fade show"
		v-show="show"
		@click="handleCancel"
	></div>
</template>

<script setup lang="ts">
import { nextTick, ref, useTemplateRef, watch } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";

/**
 * Ready-to-send Mosquito Check copy: English then Spanish, sharing one link at
 * the end (the link is language-neutral). `{name}` is substituted when the modal
 * opens so staff see, and can edit, the concrete greeting. `{link}` is left
 * literal on purpose — the backend replaces every `{link}` with the resident's
 * short link (mosquitoCheckMessage in platform/virtual_inspection.go).
 */
const MOSQUITO_CHECK_MESSAGE_TEMPLATE = `Hello {name},

Thank you for reporting a mosquito nuisance. To help us serve you, please do a mosquito self-check using the link below. It will guide you through identifying sources of mosquito production on your property, and it will help us know how best to solve your mosquito problem.

---

Hola {name}:

Gracias por reportar una molestia de mosquitos. Para poder ayudarle mejor, haga una autoevaluación de mosquitos con el enlace a continuación. Le guiará para identificar criaderos de mosquitos en su propiedad y nos ayudará a saber cuál es la mejor manera de resolver su problema de mosquitos.

{link}`;

/**
 * Fills the template in for a resident. A blank or missing name drops the name
 * and its punctuation from both greetings, so the message still opens naturally
 * instead of reading "Hello ," / "Hola :".
 */
function prefillMessage(name?: string | null): string {
	const trimmed = (name ?? "").trim();
	return MOSQUITO_CHECK_MESSAGE_TEMPLATE.replace(
		"Hello {name},",
		trimmed === "" ? "Hello," : `Hello ${trimmed},`,
	).replace("Hola {name}:", trimmed === "" ? "Hola:" : `Hola ${trimmed}:`);
}

/** The message textarea, sized to its content whenever the text changes. */
const messageBox = useTemplateRef("messageBox");

/**
 * Grows the box to fit its content so the prefilled message is readable without
 * scrolling. The height comes from scrollHeight, so it follows the actual wrap
 * width and font metrics rather than a row count. Measured after nextTick: on
 * open the prefill has to be in the DOM before it is measured, otherwise the box
 * would be sized to the previous text. max-height (see the style block) caps it
 * so a long edited message scrolls instead of pushing the modal off screen.
 */
async function fitMessageBox() {
	await nextTick();
	const box = messageBox.value;
	if (box == null) return;
	// scrollHeight excludes the border, but the inline height is border-box.
	const border = box.offsetHeight - box.clientHeight;
	box.style.height = "auto";
	box.style.height = `${box.scrollHeight + border}px`;
}

interface Emits {
	(e: "close"): void;
	(e: "sent"): void;
}
interface Props {
	communicationURI: string;
	contactName: string;
	show: boolean;
}

const emit = defineEmits<Emits>();
const props = defineProps<Props>();

const message = ref("");
const sending = ref(false);
const error = ref("");

function handleCancel() {
	error.value = "";
	emit("close");
}

async function handleSend() {
	if (!props.communicationURI) {
		error.value = "No communication selected.";
		return;
	}
	sending.value = true;
	error.value = "";
	try {
		await apiClient.JSONPost(`${props.communicationURI}/mosquito-check`, {
			send: true,
			message: message.value,
		});
		emit("sent");
		handleCancel();
	} catch (e: any) {
		error.value =
			e?.message || "Failed to send Mosquito Check. Please try again.";
	} finally {
		sending.value = false;
	}
}

watch(
	() => props.show,
	(newVal) => {
		if (newVal) {
			message.value = prefillMessage(props.contactName);
			error.value = "";
			void fitMessageBox();
		}
	},
);
</script>
